import { createAction } from '@reduxjs/toolkit';
import { MagicPrimaryAsset, MagicRef, MagicSession } from './model';
import { SavedAssetLink } from './saveToAssets/types';

// Open the studio with a snapshot of references — the basket on "Open Magic
// Studio", or an empty list from the floating pill (paste/upload from scratch).
export const opened = createAction<{ refs: MagicRef[] }, 'al/magicStudio/opened'>(
  'al/magicStudio/opened',
);

export const closed = createAction<void, 'al/magicStudio/closed'>('al/magicStudio/closed');

// The modal handing over its in-flight work as it closes — the live references
// (which become the basket again) plus the prompt / settings / current result.
// Dispatched on every way out, so a reveal or an Esc stashes just like the ✕.
export const sessionStashed = createAction<
  { refs: MagicRef[]; session: MagicSession },
  'al/magicStudio/sessionStashed'
>('al/magicStudio/sessionStashed');

// Start over: drop the basket, the stashed session and its result. Fired by the
// studio's "New session" and by the tray's "Clear" — the same reset from both
// sides, so the two surfaces can never disagree about what is staged.
export const sessionCleared = createAction<void, 'al/magicStudio/sessionCleared'>(
  'al/magicStudio/sessionCleared',
);

// Jump to an Asset Lab asset known only by its id — a reference on a history
// card, whose stored copy may predate the details that say where it lives. The
// listener resolves its project + category, then reveals it like a saved
// generation.
export const assetRevealRequested = createAction<
  { assetId: number },
  'al/magicStudio/assetRevealRequested'
>('al/magicStudio/assetRevealRequested');

// Reveal a saved generation in the finder: close the studio, then navigate to
// the project + category it was saved into and select the created asset.
// Handled by the magicStudio listener (the navigation is async — categories
// load after the project is selected).
export const revealRequested = createAction<SavedAssetLink, 'al/magicStudio/revealRequested'>(
  'al/magicStudio/revealRequested',
);

// ─── Basket ──────────────────────────────────────────────────────────
// Add the asset to the basket, or remove it if it's already staged. Dispatched
// by the per-asset toggle on the detail panel / fullscreen.
export const basketToggled = createAction<
  { asset: MagicPrimaryAsset },
  'al/magicStudio/basketToggled'
>('al/magicStudio/basketToggled');

// Stage a frame captured from the fullscreen viewer (video / 3D) — a ready-made
// upload ref carrying the captured image as a data URL.
export const basketCaptureAdded = createAction<
  { ref: MagicRef },
  'al/magicStudio/basketCaptureAdded'
>('al/magicStudio/basketCaptureAdded');

// Remove a single staged reference by its ref key (the × on a basket thumbnail).
export const basketRemoved = createAction<{ key: string }, 'al/magicStudio/basketRemoved'>(
  'al/magicStudio/basketRemoved',
);
